import { useMemo, useState } from "react";
import { Link } from "react-router";
import type { ActorDTO, GuardDecisionDTO, TaskDTO } from "@roomate/core";
import { useActors, useGuardLog } from "../api.ts";
import { Pill, Tile } from "../calm.tsx";
import { cx, ErrorNote, inputCls } from "../ui.tsx";
import {
  GUARD_DEFAULT_VERDICTS,
  GUARD_RULES,
  GUARD_VERDICTS,
  GUARD_VERDICT_LABEL,
  matchesGuardFilters,
  toggleVerdict,
  type GuardFilters,
} from "../lib/guard.ts";

const VERDICT_TONE = { block: "danger", ask: "signal", allow: "neutral", "grant-used": "live" } as const;

/**
 * The project's Guard tab (docs/specs/command-guard.md §4.5): what agents tried, what the guard did
 * about it. Opens on block and ask; a guard.* event refreshes it through the live feed.
 */
export function GuardLog({ projectId, tasks }: { projectId: string; tasks: TaskDTO[] }) {
  const actors = useActors().data;
  const [filters, setFilters] = useState<GuardFilters>({ agent: "", verdicts: GUARD_DEFAULT_VERDICTS, rule: "" });
  const query = useGuardLog(projectId, filters);
  const rows = useMemo(() => (query.data ?? []).filter((r) => matchesGuardFilters(r, filters)), [query.data, filters]);
  const taskById = useMemo(() => new Map(tasks.map((t) => [t.id, t])), [tasks]);
  const actorById = useMemo(() => new Map((actors ?? []).map((a) => [a.id, a])), [actors]);
  const agents = (actors ?? []).filter((a) => a.kind === "agent");

  return (
    <Tile span={6} title="Guard" aside={query.data ? `${rows.length} shown` : undefined}>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-3 text-[13px]" data-testid="guard-filters">
        <div role="group" aria-label="Verdict" className="flex flex-wrap gap-1.5">
          {GUARD_VERDICTS.map((v) => {
            const on = filters.verdicts.includes(v);
            return (
              <button
                key={v}
                type="button"
                aria-pressed={on}
                data-testid={`guard-verdict-${v}`}
                onClick={() => setFilters((f) => ({ ...f, verdicts: toggleVerdict(f.verdicts, v) }))}
                className={cx("rounded-full px-3 py-1 text-[12.5px] transition-colors", on ? "bg-ink text-paper" : "bg-ink/[0.05] text-ink-2 hover:bg-ink/[0.09]")}
              >
                {GUARD_VERDICT_LABEL[v]}
              </button>
            );
          })}
        </div>
        <label className="flex items-center gap-2 text-ink-2">
          <span>Agent</span>
          <select
            className={cx(inputCls, "rounded-full py-1 text-[13px]")}
            value={filters.agent ?? ""}
            onChange={(e) => setFilters((f) => ({ ...f, agent: e.target.value }))}
            data-testid="guard-agent-filter"
          >
            <option value="">All agents</option>
            {agents.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-ink-2">
          <span>Rule</span>
          <select
            className={cx(inputCls, "rounded-full py-1 font-mono text-[12.5px]")}
            value={filters.rule ?? ""}
            onChange={(e) => setFilters((f) => ({ ...f, rule: e.target.value }))}
            data-testid="guard-rule-filter"
          >
            <option value="">All rules</option>
            {GUARD_RULES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="mt-4">
        {query.error ? (
          <ErrorNote error={query.error} />
        ) : query.isLoading ? (
          <p className="text-[13.5px] text-ink-3">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="py-6 text-center text-[13.5px] text-ink-3" data-testid="guard-empty">
            No guard decisions match these filters.
          </p>
        ) : (
          <div className="-mx-5 overflow-x-auto px-5 md:-mx-6 md:px-6">
            <table className="w-full min-w-[720px] text-left text-[12.5px]" data-testid="guard-table">
              <thead className="border-b border-rule text-ink-3">
                <tr>
                  <th className="py-2 pr-4 font-medium">Time</th>
                  <th className="py-2 pr-4 font-medium">Agent</th>
                  <th className="py-2 pr-4 font-medium">Task</th>
                  <th className="py-2 pr-4 font-medium">Verdict</th>
                  <th className="py-2 pr-4 font-medium">Rule</th>
                  <th className="py-2 font-medium">Command</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <GuardRow key={row.id} row={row} agent={row.agentId ? actorById.get(row.agentId) : undefined} task={row.taskId ? taskById.get(row.taskId) : undefined} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Tile>
  );
}

export function GuardRow({ row, agent, task }: { row: GuardDecisionDTO; agent: ActorDTO | undefined; task: TaskDTO | undefined }) {
  return (
    <tr className="border-b border-rule/50 align-top" data-testid="guard-row" data-verdict={row.verdict}>
      <td className="whitespace-nowrap py-2.5 pr-4 tabular-nums text-ink-3">{formatWhen(row.ts)}</td>
      <td className="whitespace-nowrap py-2.5 pr-4 text-ink-2">{agent?.name ?? (row.agentId ? row.agentId.slice(0, 10) : "—")}</td>
      <td className="max-w-[18ch] truncate py-2.5 pr-4">
        {task ? (
          <Link to={`/tasks/${task.id}`} className="text-ink-2 underline decoration-rule hover:text-ink" title={task.title}>
            #{task.number}
          </Link>
        ) : (
          <span className="text-ink-3">—</span>
        )}
      </td>
      <td className="whitespace-nowrap py-2.5 pr-4">
        <Pill tone={VERDICT_TONE[row.verdict]} testId="guard-row-verdict">
          {GUARD_VERDICT_LABEL[row.verdict]}
          {row.mode === "audit" && " · audit"}
        </Pill>
      </td>
      <td className="whitespace-nowrap py-2.5 pr-4 font-mono text-[12px] text-ink-2">{row.rules.join(", ") || "—"}</td>
      <td className="max-w-[48ch] truncate py-2.5 font-mono text-[12px] text-ink" title={row.command} data-testid="guard-row-command">
        {row.command}
      </td>
    </tr>
  );
}

function formatWhen(ts: number): string {
  return new Date(ts).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
}
